import { useAppSelector } from "integrations/redux/hooks"
import { Licenses } from "@nova/constants"
import { downloadUri } from "tools/browser/download"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectSatelliteFlightById } from "features/entity-caching/flights/selectors"
import {
    DownloadButtonData,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"
import { useCopyResourceLink } from "plugins/shared/useCopyResourceLink"
import { finished, satelliteOutputFolder } from "@nova/util-s3"
import { fetchApiGateway, getApiGatewayDomain } from "@nova/fetch-api-gateway"
import { selectActiveCogOrtho } from "./layers/satelliteLayer"

export const useSatelliteDownloadButtonData = (flightId: string): DownloadButtonData => {
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const flight = useAppSelector(selectSatelliteFlightById(flightId))
    const fire = useAppSelector(selectFireById(fireId))!

    const cogId = flight && selectActiveCogOrtho(flight)?.fileId
    const isDemo = !!flight?.demoId
    const folder = satelliteOutputFolder(workspaceId, fireId, flightId)

    const getFilename = (end: string) => `${fire.name} ${flight?.name} - ${end}`

    const copyResourceLink = useCopyResourceLink({ workspaceId, fireId, flightId, isDemo })

    const downloadTiff = async () => {
        const filename = getFilename("Orthophoto.tif")
        const s3Key = finished(folder, undefined, undefined, cogId)
        const url = await createS3DownloadUrlInBrowser(s3Key, isDemo)
        await downloadUri(filename, url)
    }

    const downloadCogExport = async (
        format: "jpeg" | "png" | "webp" | "kmz" | "tiff",
        maxSize?: number
    ) => {
        const ext = { jpeg: "jpg", png: "png", webp: "webp", kmz: "kmz", tiff: "tif" } as const
        const filename = getFilename(`Orthophoto.${ext[format]}`)
        const params = new URLSearchParams({
            format,
            isDemo: String(isDemo),
            filename,
            source: "satellite",
        })
        if (cogId) params.set("cogId", cogId)
        if (maxSize) params.set("maxSize", String(maxSize))
        const url = `${getApiGatewayDomain()}/org/${workspaceId}/project/${fireId}/flight/${flightId}/cog-export?${params}`
        downloadUri(filename, url)
    }

    const generateStableKmz = () => {
        const params = new URLSearchParams({
            format: "kmz",
            stable: "true",
            isDemo: String(isDemo),
            maxSize: "2000",
            source: "satellite",
        })
        if (cogId) params.set("cogId", cogId)
        return fetchApiGateway("get /org/:orgId/project/:projectId/flight/:flightId/cog-export", {
            params: { orgId: workspaceId, projectId: fireId, flightId },
            query: params.toString(),
        })
    }

    if (!flight || !cogId) return { exportOptions: {} }

    const exportOptions: NestedDownloadData = {
        Ortho: {
            TIFF: { callback: downloadTiff, restrictedLicenses: [Licenses.Lite] },
            ["JPG (Medium)"]: {
                callback: () => downloadCogExport("jpeg", 8000),
                restrictedLicenses: [Licenses.Lite],
            },
            ["SMALL TIFF"]: {
                callback: () => downloadCogExport("tiff"),
                restrictedLicenses: [Licenses.Lite],
            },
            JPG: { callback: () => downloadCogExport("jpeg"), restrictedLicenses: [Licenses.Lite] },
            PNG: { callback: () => downloadCogExport("png"), restrictedLicenses: [Licenses.Lite] },
            KMZ: {
                callback: () => downloadCogExport("kmz"),
                copyCallback: (id: string) =>
                    copyResourceLink(id, "arc-ortho.kmz", () => generateStableKmz()),
                restrictedLicenses: [Licenses.Lite],
            },
        },
    }

    return { exportOptions }
}
